// Focus return of the Radix dialog, alert dialog and sheet wrappers: Radix gives focus back on close only to a Dialog.Trigger, and the app opens every overlay from state, so without this the focus fell to the page body; the element focused when the overlay opened gets it back (when still in the page; for an overlay opened from a menu item, the button of that menu, since the item leaves with the menu), and the caller's own open and close handlers keep running first.
import { useRef } from "react";

interface AutoFocusHandlers {
  onOpenAutoFocus?: ((event: Event) => void) | undefined;
  onCloseAutoFocus?: ((event: Event) => void) | undefined;
}

function focusedOpener(): HTMLElement | null {
  const active = document.activeElement;
  if (!(active instanceof HTMLElement) || active === document.body) {
    return null;
  }
  const label = active.closest('[role="menu"]')?.getAttribute("aria-labelledby");
  const menuButton = label ? document.getElementById(label) : null;
  return menuButton ?? active;
}

export function useReturnFocus({ onOpenAutoFocus, onCloseAutoFocus }: AutoFocusHandlers): {
  onOpenAutoFocus: (event: Event) => void;
  onCloseAutoFocus: (event: Event) => void;
} {
  const opener = useRef<HTMLElement | null>(null);
  return {
    onOpenAutoFocus: (event) => {
      opener.current = focusedOpener();
      onOpenAutoFocus?.(event);
    },
    onCloseAutoFocus: (event) => {
      onCloseAutoFocus?.(event);
      const target = opener.current;
      opener.current = null;
      if (!event.defaultPrevented && target?.isConnected === true) {
        event.preventDefault();
        target.focus({ preventScroll: true });
      }
    },
  };
}
